import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Persist the sidebar's scrollTop across route changes.
 *
 * Each page in Talmia wraps its own `<Layout>`, so React unmounts + remounts
 * the Sidebar on every navigation and — without this hook — would reset the
 * user's menu scroll position back to 0 every click. Storage is
 * sessionStorage (per-tab), rAF-throttled writes, restore in useLayoutEffect
 * so the user never sees the flash.
 */
const SCROLL_KEY = "talmia.sidebar.scrollTop.v1";

export function useSidebarScrollPreserve() {
  const scrollRef = useRef(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const raw = sessionStorage.getItem(SCROLL_KEY);
    if (raw !== null) {
      const y = parseInt(raw, 10);
      if (!Number.isNaN(y)) el.scrollTop = y;
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        try { sessionStorage.setItem(SCROLL_KEY, String(el.scrollTop)); } catch { /* quota */ }
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return scrollRef;
}
